// assets/stamp-studio.js
// Orchestrates the 3-panel custom-stamp studio wizard: pick a real physical
// size -> finish the design (wraps the freeform stamp-designer-component) ->
// pick which physical stamp is available at that size. Size is picked FIRST
// and drives the editor's die shape via setStampSize(); the studio does NOT
// reimplement any design/rendering logic — it reads the editor's own hidden
// `properties[...]` inputs at hand-off and gates progress on the editor's
// bubbling `stamp:proof-confirmed` event.
import { Component } from '@theme/component';
import { STAMP_SIZES, isSmallSize } from '@theme/stamp-sizes';
import { getTemplate } from '@theme/stamp-templates';

const STORAGE_PREFIX = 'stampDesign:';
const STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Advisory-only threshold (combined characters across the visible text
// fields) — not a hard limit. Real per-size manufacturing fit constraints
// are a deferred follow-up; this is just a plain-language nudge.
const LONG_TEXT_THRESHOLD = 18;

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

class StampStudio extends Component {
  /** @type {import('@theme/stamp-sizes').StampSize | null} */
  #selectedSize = null;
  /** @type {boolean} */
  #proofConfirmed = false;
  #lastTextLength = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#pruneOldDesigns();
    this.#buildSizeGrid();
    this.addEventListener('stamp:proof-confirmed', () => this.#onProofConfirmed());
    // The editor reports its total text length as the design changes; drive the
    // small-size advisory off that event instead of reading its inputs.
    this.addEventListener('stamp:design-changed', (e) => {
      this.#lastTextLength = (e.detail && e.detail.textLength) || 0;
      this.#checkSizeAdvisory();
    });
    this.#showPanel('size');
  }

  #designerEl() {
    return this.querySelector('stamp-designer-component');
  }

  /** Restrict the size list to a loaded template's shape, else show all. */
  #templateShape() {
    const id = new URLSearchParams(location.search).get('template');
    if (!id) return null;
    const tpl = getTemplate(id);
    if (!tpl) return null;
    return tpl.shape === 'rectangle' || tpl.shape === 'for' ? 'rectangle' : 'round';
  }

  #buildSizeGrid() {
    const grid = this.refs.sizeGrid;
    if (!grid) return;
    const only = this.#templateShape();
    const groups = [
      { key: 'round', label: 'Round' },
      { key: 'rectangle', label: 'Rectangle & square' },
    ].filter((g) => !only || g.key === only);
    grid.innerHTML = groups
      .map((g) => {
        const sizes = STAMP_SIZES.filter((s) => s.shape === g.key);
        if (!sizes.length) return '';
        const btns = sizes
          .map(
            (s) =>
              `<button type="button" class="stamp-studio__size-btn" data-size="${s.value}" on:click="/handleSizePick">${escapeHtml(s.label)}</button>`
          )
          .join('');
        return `<div class="stamp-studio__size-group"><p class="stamp-studio__size-grouplabel">${g.label}</p><div class="stamp-studio__size-row">${btns}</div></div>`;
      })
      .join('');
  }

  /** @param {Event} event */
  handleSizePick(event) {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-size]');
    if (!btn) return;
    const size = STAMP_SIZES.find((s) => s.value === btn.dataset.size);
    if (!size) return;
    this.#selectedSize = size;
    this.querySelectorAll('.stamp-studio__size-btn').forEach((b) => b.classList.toggle('is-selected', b === btn));
    // Drive the editor's die shape/proportions from the chosen physical size.
    this.#designerEl()?.setStampSize({ shape: size.shape, label: size.label, mm: size.mm });
    this.#showPanel('design');
    this.#checkSizeAdvisory();
  }

  /**
   * Plain-language, non-blocking nudge when a small size is paired with
   * comparatively long text — not a hard character limit (see
   * LONG_TEXT_THRESHOLD's comment above).
   */
  #checkSizeAdvisory() {
    const el = this.refs.sizeAdvisory;
    if (!el) return;
    if (!this.#selectedSize || !isSmallSize(this.#selectedSize)) {
      el.hidden = true;
      return;
    }
    if ((this.#lastTextLength || 0) >= LONG_TEXT_THRESHOLD) {
      el.textContent = `This is a small stamp (${this.#selectedSize.label}) — you may want to shorten your text so it fits comfortably.`;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  handleBackToSize() {
    this.#showPanel('size');
  }

  handleBackToDesign() {
    this.#showPanel('design');
  }

  #onProofConfirmed() {
    this.#proofConfirmed = true;
    const btn = this.refs.continueToProductBtn;
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Choose your stamp →';
    }
  }

  handleContinueToProduct() {
    if (!this.#proofConfirmed || !this.#selectedSize) return;
    this.#showPanel('product');
    this.#loadProducts();
  }

  /** @param {'size'|'design'|'product'} name */
  #showPanel(name) {
    const { sizePanel, designPanel, productPanel, stepDots } = this.refs;
    if (sizePanel) sizePanel.hidden = name !== 'size';
    if (designPanel) designPanel.hidden = name !== 'design';
    if (productPanel) productPanel.hidden = name !== 'product';
    const order = ['size', 'design', 'product'];
    (stepDots ?? []).forEach((el, i) => el.classList.toggle('is-active', i <= order.indexOf(name)));
  }

  async #loadProducts() {
    const size = this.#selectedSize;
    const { productGrid, productSubtitle } = this.refs;
    if (!size || !productGrid) return;
    if (productSubtitle) productSubtitle.textContent = `Stamps available in ${size.label}`;
    productGrid.innerHTML = '<p class="stamp-studio__status">Loading stamps…</p>';
    try {
      const res = await fetch(`/collections/${size.collectionHandle}/products.json`);
      if (!res.ok) throw new Error('bad response');
      const data = await res.json();
      const products = Array.isArray(data.products) ? data.products : [];
      if (products.length === 0) {
        productGrid.innerHTML =
          '<p class="stamp-studio__status">Nothing available at this size yet — check back soon, or go back and pick a different size.</p>';
        return;
      }
      productGrid.innerHTML = products
        .map((p) => {
          const price = p.variants?.[0]?.price ?? '';
          return `
            <div class="stamp-studio__product-card">
              <p class="stamp-studio__product-title">${escapeHtml(p.title)}</p>
              <p class="stamp-studio__product-price">₹${escapeHtml(price)}</p>
              <div class="stamp-studio__product-desc">${p.body_html || ''}</div>
              <button type="button" class="stamp-studio__product-select" data-handle="${escapeHtml(p.handle)}" on:click="/handleSelectProduct">Select this stamp</button>
            </div>`;
        })
        .join('');
    } catch (e) {
      productGrid.innerHTML =
        '<p class="stamp-studio__status">Something went wrong loading stamps for this size. Please try again.</p>';
    }
  }

  /** @param {Event} event */
  handleSelectProduct(event) {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-handle]');
    const handle = btn?.dataset.handle;
    if (!handle) return;

    const designerEl = this.#designerEl();
    /** @type {Record<string, string>} */
    const props = {};
    designerEl?.querySelectorAll('[name^="properties\\["]').forEach((el) => {
      const match = /** @type {HTMLInputElement} */ (el).name.match(/^properties\[(.+)\]$/);
      const value = /** @type {HTMLInputElement} */ (el).value;
      if (match && value) props[match[1]] = value;
    });
    if (this.#selectedSize) props['Size'] = this.#selectedSize.label;

    const sid =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : Math.random().toString(36).slice(2, 10);

    try {
      localStorage.setItem(STORAGE_PREFIX + sid, JSON.stringify({ ts: Date.now(), props }));
    } catch (e) {
      // Private browsing / quota exceeded — degrade gracefully, the text
      // fields below still ride through via the URL.
    }

    /** @type {Record<string, string>} */
    const textProps = {};
    Object.keys(props).forEach((key) => {
      if (!key.startsWith('_')) textProps[key] = props[key];
    });

    const url = `/products/${handle}?sid=${encodeURIComponent(sid)}&d=${encodeURIComponent(JSON.stringify(textProps))}`;
    window.location.href = url;
  }

  #pruneOldDesigns() {
    try {
      const now = Date.now();
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
        try {
          const entry = JSON.parse(localStorage.getItem(key) || '{}');
          if (!entry.ts || now - entry.ts > STORAGE_MAX_AGE_MS) localStorage.removeItem(key);
        } catch (e) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      /* localStorage unavailable — nothing to prune */
    }
  }
}

if (!customElements.get('stamp-studio-component')) {
  customElements.define('stamp-studio-component', StampStudio);
}
